import { I18n } from '@calendly/i18n';
import { RadioInput } from '@calendly/ui/components/radio-input';
import { BodyText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { AllSchedulingMethods, SchedulingMethods } from 'constants/meetings';
import { useFetchCalendarAccountsQuery } from 'features/calendar_connections';
import { LeftColumn } from './left_column';
import { RightColumn } from './right_column';
import { CheckboxContainer as RadioContainer, LabelContainer, OptionWrapper, StyledLabel } from './styles';
const {
  ALL,
  CALENDLY,
  GOOGLE,
  OUTLOOK
} = SchedulingMethods;
const radioLeftColumnWrapperIsPhoneCss = "r1fycht2";
const radioLeftColumnWrapperCss = "r8e0g86";
const RadioLeftColumnWrapper = forwardRef((props, ref) => {
  const isPhone = useDeviceType('isPhone');
  return <LeftColumn ref={ref} {...props} className={classNames(radioLeftColumnWrapperCss, {
    [radioLeftColumnWrapperIsPhoneCss]: isPhone
  }, props.className)} />;
});
const radioRightColumnWrapperIsPhoneCss = "rkomtcu";
const radioRightColumnWrapperCss = "rp18qex";
const RadioRightColumnWrapper = forwardRef((props, ref) => {
  const isPhone = useDeviceType('isPhone');
  return <RightColumn ref={ref} {...props} className={classNames(radioRightColumnWrapperCss, {
    [radioRightColumnWrapperIsPhoneCss]: isPhone
  }, props.className)} />;
});
const radioInputLabelCss = "r1svpmu3";
const RadioInputLabel = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'label';
  return <Component ref={ref} {...restProps} className={classNames(radioInputLabelCss, props.className)} />;
});
const radioLabelCss = "r5m1dkj";
const RadioLabel = forwardRef((props, ref) => <StyledLabel ref={ref} {...props} className={classNames(radioLabelCss, props.className)} />);
const styledRadioInputCss = "s1qzegz5";
const StyledRadioInput = forwardRef((props, ref) => <RadioInput ref={ref} {...props} className={classNames(styledRadioInputCss, props.className)} />);
const CONNECTED_CALENDAR_ORDER = [GOOGLE, OUTLOOK];
const KNOWN_KINDS = new Set(CONNECTED_CALENDAR_ORDER);
function getPushCalendarEntities(data) {
  const ids = data?.ids ?? [];
  const entities = data?.entities ?? {};
  return ids.map(id => entities[id]).filter(e => e?.pushCalendarId);
}

/** One option per connected push calendar type (Google and/or Outlook), in fixed order. */
function getConnectedCalendarOptions(data, schedulingMethods) {
  const entities = getPushCalendarEntities(data);
  const byMethod = {};
  for (const entity of entities) {
    const method = String(entity.kind ?? '').toLowerCase();
    if (KNOWN_KINDS.has(method) && byMethod[method] === undefined) {
      byMethod[method] = entity.calendarName ?? entity.name ?? null;
    }
  }
  const options = CONNECTED_CALENDAR_ORDER.filter(method => method in byMethod).map(method => ({
    method,
    name: byMethod[method]
  }));
  if (options.length > 0) return options;
  const onlyOutlookSelected = schedulingMethods.length === 1 && schedulingMethods.includes(OUTLOOK);
  return [{
    method: onlyOutlookSelected ? OUTLOOK : GOOGLE,
    name: null
  }];
}
function isAllSelected(schedulingMethods) {
  return schedulingMethods.length === 0 || AllSchedulingMethods.every(method => schedulingMethods.includes(method));
}
function isOnlyMethodSelected(schedulingMethods, method) {
  return schedulingMethods.length === 1 && schedulingMethods.includes(method);
}
export const SchedulingMethod = ({
  params,
  onChange
}) => {
  const {
    scheduling_methods: schedulingMethods
  } = params;
  const {
    data
  } = useFetchCalendarAccountsQuery();
  const connectedOptions = getConnectedCalendarOptions(data, schedulingMethods);
  const onSelect = value => {
    onChange({
      ...params,
      scheduling_methods: value === ALL ? AllSchedulingMethods : [value]
    });
  };
  const allChecked = isAllSelected(schedulingMethods);
  const isSelected = method => isOnlyMethodSelected(schedulingMethods, method);
  return <>
      <RadioLeftColumnWrapper>
        <LabelContainer>
          <BodyText weight="bold">{I18n.t('publisher.meetings.filter_modal.scheduling_method.label')}</BodyText>
        </LabelContainer>
      </RadioLeftColumnWrapper>
      <RadioRightColumnWrapper>
        <RadioContainer>
          <OptionWrapper>
            <RadioInputLabel>
              <StyledRadioInput checked={allChecked} onChange={() => onSelect(ALL)} id="scheduling_method_all" />
              <RadioLabel htmlFor="scheduling_method_all">{I18n.t('publisher.meetings.filter_modal.scheduling_method.all')}</RadioLabel>
            </RadioInputLabel>
          </OptionWrapper>
          <OptionWrapper>
            <RadioInputLabel>
              <StyledRadioInput checked={isSelected(CALENDLY)} onChange={() => onSelect(CALENDLY)} id="scheduling_method_calendly" />
              <RadioLabel htmlFor="scheduling_method_calendly">
                {I18n.t('publisher.meetings.filter_modal.scheduling_method.calendly')}
              </RadioLabel>
            </RadioInputLabel>
          </OptionWrapper>
          {connectedOptions.map(({
          method,
          name
        }) => <OptionWrapper key={method}>
              <RadioInputLabel>
                <StyledRadioInput checked={isSelected(method)} onChange={() => onSelect(method)} id={`scheduling_method_${method}`} />
                <RadioLabel htmlFor={`scheduling_method_${method}`}>
                  {name ? I18n.t('publisher.meetings.filter_modal.scheduling_method.connected_calendar', {
                name
              }) : I18n.t('publisher.meetings.filter_modal.scheduling_method.connected_calendar_default')}
                </RadioLabel>
              </RadioInputLabel>
            </OptionWrapper>)}
        </RadioContainer>
      </RadioRightColumnWrapper>
    </>;
};

require("./scheduling_method.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./scheduling_method.js");